import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Table,
  Card,
  Row,
  Col,
  Select,
  Input,
  Tooltip,
  Alert,
  Typography,
  DatePicker,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;
import dayjs from 'dayjs';
import ClassificationTag from '../components/ClassificationTag';
import { getCases } from '../services/api';
import { useBranch } from '../components/BranchContext';

const { Text } = Typography;

const CLASSIFICATION_OPTIONS = [
  { value: 'No Changes', label: 'No Changes' },
  { value: 'Minor', label: 'Minor' },
  { value: 'Major', label: 'Major' },
  { value: 'Incidental', label: 'Incidental' },
];

const Cases = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const yesterday = dayjs().subtract(1, 'day');
  const today = dayjs();
  const initialFrom = searchParams.get('date_from');
  const initialTo = searchParams.get('date_to');

  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [pageSize] = useState(20);
  const [classification, setClassification] = useState(
    searchParams.getAll('classification') || []
  );
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [dateRange, setDateRange] = useState([
    initialFrom ? dayjs(initialFrom) : yesterday,
    initialTo ? dayjs(initialTo) : yesterday,
  ]);

  const { branchParam } = useBranch();

  const fetchCases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: pageSize };
      if (classification.length > 0) params.classification = classification.join(',');
      if (search) params.search = search;
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.date_from = dateRange[0].format('YYYY-MM-DD');
        params.date_to = dateRange[1].format('YYYY-MM-DD');
      }
      if (branchParam) params.branch = branchParam;

      const result = await getCases(params);
      setData(result.cases || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load cases');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, classification, search, dateRange, branchParam]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  useEffect(() => {
    const params = {};
    if (page > 1) params.page = page;
    if (classification.length > 0) params.classification = classification;
    if (search) params.search = search;
    if (dateRange && dateRange[0] && dateRange[1]) {
      params.date_from = dateRange[0].format('YYYY-MM-DD');
      params.date_to = dateRange[1].format('YYYY-MM-DD');
    }
    setSearchParams(params, { replace: true });
  }, [page, classification, search, dateRange, setSearchParams]);

  const columns = [
    {
      title: 'Accession #',
      dataIndex: 'accession_number',
      key: 'accession_number',
      width: 130,
      render: (text, record) => (
        <a
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/cases/${record.study_fk}`);
          }}
          style={{ cursor: 'pointer' }}
        >
          {text || '--'}
        </a>
      ),
    },
    {
      title: 'Study Name',
      dataIndex: 'study_desc',
      key: 'study_desc',
      ellipsis: true,
      width: 220,
    },
    {
      title: 'Classification',
      dataIndex: 'classification',
      key: 'classification',
      width: 200,
      render: (text) => <ClassificationTag classification={text} />,
    },
    {
      title: 'Comment',
      dataIndex: 'classification_comment',
      key: 'classification_comment',
      ellipsis: true,
      width: 220,
      render: (text) => (
        <Tooltip title={text}>
          <Text style={{ fontSize: 12 }}>{text || '--'}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Study Date',
      dataIndex: 'study_date',
      key: 'study_date',
      width: 120,
      render: (text) => (text ? dayjs(text).format('DD MMM YYYY') : '--'),
      sorter: (a, b) => {
        const da = a.study_date ? dayjs(a.study_date).unix() : 0;
        const db = b.study_date ? dayjs(b.study_date).unix() : 0;
        return da - db;
      },
    },
  ];

  if (error) {
    return <Alert type="error" message="Error" description={error} showIcon />;
  }

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={7}>
            <Input
              placeholder="Search accession # or patient..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Classification"
              mode="multiple"
              style={{ width: '100%' }}
              allowClear
              value={classification}
              onChange={(val) => {
                setClassification(val || []);
                setPage(1);
              }}
              options={CLASSIFICATION_OPTIONS}
            />
          </Col>
          <Col xs={24} sm={12} md={7}>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={(val) => {
                setDateRange(val);
                setPage(1);
              }}
              allowClear={false}
              format="YYYY-MM-DD"
              presets={[
                { label: 'Yesterday → Today', value: [yesterday, today] },
                { label: 'Yesterday', value: [yesterday, yesterday] },
                { label: 'Today', value: [today, today] },
                { label: 'Last 7 days', value: [dayjs().subtract(7, 'day'), today] },
                { label: 'Last 30 days', value: [dayjs().subtract(30, 'day'), today] },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Text type="secondary" style={{ lineHeight: '32px' }}>
              {total.toLocaleString()} results
            </Text>
          </Col>
        </Row>
      </Card>

      <Card size="small">
        <Table
          dataSource={data}
          columns={columns}
          rowKey={(record) => record.id || record.study_fk}
          loading={loading}
          size="small"
          scroll={{ x: 1100 }}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: false,
            showTotal: (t, range) => `${range[0]}-${range[1]} of ${t.toLocaleString()}`,
            onChange: (p) => setPage(p),
          }}
          onRow={(record) => ({
            onClick: () => navigate(`/cases/${record.study_fk}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>
    </div>
  );
};

export default Cases;
